import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AdSlot } from "@/components/AdSlot";
import { Droplet } from "lucide-react";
import { toast } from "sonner";

export default function Login({ admin = false }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(admin && (u?.role === "admin" || u?.role === "owner") ? "/admin" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #451a03, #78350f 55%, #b45309)" }}>
        <Link to="/" className="flex items-center gap-2 text-amber-100 relative z-10">
          <Droplet className="w-7 h-7" style={{ color: "#F59E0B" }} fill="#F59E0B" />
          <span className="font-display text-3xl font-bold">Drops of Honey</span>
        </Link>
        <div className="relative z-10 text-amber-50">
          <h2 className="font-display text-5xl font-bold leading-tight">The collector's home for antique glass insulators.</h2>
          <p className="mt-4 text-amber-100/80 max-w-md">Catalog your finds, grade every piece, connect with collectors worldwide, and chase that grail-color drop of honey.</p>
        </div>
        <div className="relative z-10 max-w-xs">
          <AdSlot position="login" label="Sponsored" />
        </div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.35), transparent 70%)" }} />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm float-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Droplet className="w-7 h-7 brand-text" fill="currentColor" />
            <span className="font-display text-2xl font-bold">Drops of Honey</span>
          </div>
          <h1 className="font-display text-4xl font-bold">{admin ? "Administrator sign in" : "Sign in"}</h1>
          <p className="text-muted-page mt-1 mb-6">{admin ? "Sign in with your administrator account." : "Welcome back, collector."}</p>

          {error && <div data-testid="login-error" className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}

          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Email</label>
          <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full surface border rounded-lg px-3 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400" />

          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Password</label>
          <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full surface border rounded-lg px-3 py-2.5 mb-2 focus:outline-none focus:ring-2 focus:ring-amber-400" />

          <div className="text-right mb-6">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm brand-text font-semibold honey-underline">Forgot password?</Link>
          </div>

          <button data-testid="login-submit-btn" disabled={loading} className="w-full py-3 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--brand)", color: "#fff" }}>
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-sm text-muted-page mt-4 text-center">
            New here? <Link to="/register" className="brand-text font-semibold honey-underline">Create an account</Link>
          </p>
          <p className="text-sm text-muted-page mt-2 text-center">
            {admin ? (
              <Link to="/login" data-testid="member-signin-link" className="brand-text font-semibold honey-underline">Member sign in</Link>
            ) : (
              <Link to="/admin-login" data-testid="admin-signin-link" className="brand-text font-semibold honey-underline">Administrator sign in</Link>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
